import { useState } from "react";
import { IconDeviceFloppy, IconX } from "@tabler/icons-react";
import { Button, Card, Divider, Group, Heading, Input, NumberInput, Stack, Textarea } from "@/components/ui";
import { toastError, toastExito } from "@/lib/toast";
import type {
  EntradaAlmacenAdmin,
  EntradaAlmacenInput,
  OrdenCompraAdmin,
  OrdenCompraInput,
} from "@/lib/types";
import { extractApiMessage } from "@/shared/api/extract-message";
import { ConceptosEditor } from "@/features/admin-compras-shared/ConceptosEditor";
import { EntradasEditor } from "@/features/admin-compras-shared/EntradasEditor";
import { ProveedorFields, proveedorVacio } from "@/features/admin-compras-shared/ProveedorFields";
import { useActualizarOrdenCompra, useCrearOrdenCompra } from "../hooks/use-ordenes-compra";
import { previewEntradaXml } from "../services/ordenes-compra-service";

function ordenAInput(oc: OrdenCompraAdmin): OrdenCompraInput {
  return {
    division: oc.division,
    estatus: oc.estatus,
    fecha: oc.fecha,
    subTotal: oc.subTotal,
    impuestos: oc.impuestos,
    total: oc.total,
    moneda: oc.moneda,
    observaciones: oc.observaciones,
    proveedor: { ...oc.proveedor },
    conceptos: oc.conceptos.map((c) => ({
      lineaPedido: c.lineaPedido, cantidad: c.cantidad, unidad: c.unidad,
      noIdentificacion: c.noIdentificacion, descripcion: c.descripcion,
      valorUnitario: c.valorUnitario, importe: c.importe, observaciones: c.observaciones,
      centroCostos: c.centroCostos, descCecos: c.descCecos, cuentaContable: c.cuentaContable,
      impuestos: c.impuestos.map((x) => ({ tipo: x.tipo, indicador: x.indicador, monto: x.monto, porcentaje: x.porcentaje })),
    })),
    entradas: oc.entradas.map(entradaAInput),
  };
}

/** Mapea una entrada de almacén (DTO admin) a su input editable. */
function entradaAInput(e: EntradaAlmacenAdmin): EntradaAlmacenInput {
  return {
    folio: e.folio, claveOrdenCompra: e.claveOrdenCompra, fecha: e.fecha,
    subTotal: e.subTotal, impuestos: e.impuestos, total: e.total, moneda: e.moneda,
    anula: e.anula, referencia: e.referencia, observaciones: e.observaciones,
    conceptos: e.conceptos.map((c) => ({
      lineaPedido: c.lineaPedido, cantidad: c.cantidad, unidad: c.unidad,
      noIdentificacion: c.noIdentificacion, descripcion: c.descripcion,
      valorUnitario: c.valorUnitario, importe: c.importe, observaciones: c.observaciones,
      centroCostos: null, descCecos: null, cuentaContable: null,
      impuestos: c.impuestos.map((x) => ({ tipo: x.tipo, indicador: x.indicador, monto: x.monto, porcentaje: x.porcentaje })),
    })),
  };
}

/**
 * Valida si una entrada importada puede cargarse contra las entradas ya presentes.
 * Devuelve un mensaje de error si NO se permite, o null si es válida.
 *
 * Reglas (flujo de entradas de almacén):
 *  · Una entrada es "anuladora" si trae `anula` (folio de la entrada que cancela).
 *    Para cargar una anuladora, la entrada que anula debe existir ya.
 *  · Para una recepción normal: si una línea (lineaPedido) ya está cubierta por una
 *    recepción ACTIVA (no anuladora y no anulada), no se permite · para actualizarla
 *    primero hay que cargar una entrada anuladora de la recepción previa.
 */
function validarEntradaImportada(
  nueva: EntradaAlmacenAdmin,
  existentes: EntradaAlmacenInput[]
): string | null {
  const folioNuevo = nueva.folio?.trim() ?? "";
  if (folioNuevo && existentes.some((e) => e.folio.trim() === folioNuevo))
    return `Ya existe una entrada con el folio ${folioNuevo} en esta orden de compra.`;

  const anula = nueva.anula?.trim() ?? "";
  if (anula) {
    // Entrada anuladora · la entrada que anula debe existir previamente.
    const existe = existentes.some((e) => e.folio.trim() === anula);
    return existe
      ? null
      : `Para cargar una entrada anuladora debe existir primero la entrada [${anula}] que se desea anular.`;
  }

  // Recepción normal · revisar colisión de líneas con recepciones activas.
  const foliosAnulados = new Set(
    existentes.filter((e) => e.anula?.trim()).map((e) => e.anula!.trim())
  );
  const lineasNuevas = new Set(
    nueva.conceptos.map((c) => c.lineaPedido.trim()).filter(Boolean)
  );
  for (const e of existentes) {
    if (e.anula?.trim()) continue; // las anuladoras no cubren líneas activas
    if (foliosAnulados.has(e.folio.trim())) continue; // recepción ya anulada → actualizable
    for (const c of e.conceptos) {
      const linea = c.lineaPedido.trim();
      if (linea && lineasNuevas.has(linea))
        return `La línea ${linea} ya tiene una entrada activa (folio ${e.folio}). Para actualizarla, primero carga una entrada anuladora de esa entrada.`;
    }
  }
  return null;
}

function inputVacio(): OrdenCompraInput {
  return {
    division: null, estatus: "1", fecha: null, subTotal: 0, impuestos: 0, total: 0,
    moneda: "MXN", observaciones: null, proveedor: proveedorVacio(), conceptos: [], entradas: [],
  };
}

interface Props {
  inicial: OrdenCompraAdmin | null; // null = crear
  onDone: () => void;
}

export function OrdenCompraForm({ inicial, onDone }: Props) {
  const esNuevo = inicial === null;
  const [clave, setClave] = useState(inicial?.clave ?? "");
  const [form, setForm] = useState<OrdenCompraInput>(inicial ? ordenAInput(inicial) : inputVacio());

  const crear = useCrearOrdenCompra({ onOk: onDone });
  const actualizar = useActualizarOrdenCompra({ onOk: onDone });
  const guardando = crear.isPending || actualizar.isPending;
  const [importando, setImportando] = useState(false);

  const upd = (patch: Partial<OrdenCompraInput>) => setForm((f) => ({ ...f, ...patch }));

  // Importa una o varias entradas desde sus XML · el server las parsea y valida (parseable,
  // con folio y misma clave de OC) y el form valida el flujo (anuladora/línea). Se procesan
  // en orden numérico/alfabético por nombre de archivo (importa para el flujo anular→
  // recepción). Las válidas se agregan al editor y se persisten al guardar la OC.
  const importarEntrada = async (files: File[]) => {
    if (!clave.trim()) {
      toastError({ title: "Falta la clave", message: "Captura la clave de la OC antes de importar entradas." });
      return;
    }
    const ordenados = [...files].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
    );

    setImportando(true);
    const agregadas: EntradaAlmacenInput[] = [];
    const errores: string[] = [];
    try {
      // Se valida contra las entradas existentes + las agregadas en este mismo lote,
      // respetando el orden (una anuladora requiere que su recepción ya esté presente).
      let acumuladas = [...form.entradas];
      for (const file of ordenados) {
        try {
          const entrada = await previewEntradaXml(clave.trim(), file);
          const error = validarEntradaImportada(entrada, acumuladas);
          if (error) {
            errores.push(`${file.name}: ${error}`);
            continue;
          }
          const input = entradaAInput(entrada);
          acumuladas = [...acumuladas, input];
          agregadas.push(input);
        } catch (e) {
          errores.push(`${file.name}: ${extractApiMessage(e, "no se pudo importar")}`);
        }
      }

      if (agregadas.length > 0) {
        setForm((f) => ({ ...f, entradas: [...f.entradas, ...agregadas] }));
        toastExito({
          title: `${agregadas.length} entrada(s) importada(s)`,
          message: "Revisa los datos y guarda la OC para persistirlas.",
        });
      }
      if (errores.length > 0) {
        toastError({
          title: `${errores.length} entrada(s) no se cargaron`,
          message: (
            <>
              {errores.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            </>
          ),
        });
      }
    } finally {
      setImportando(false);
    }
  };

  const submit = () => {
    if (!clave.trim()) {
      toastError({ title: "Falta la clave", message: "La clave de la OC es obligatoria." });
      return;
    }
    if (!form.proveedor.nombre.trim() || !form.proveedor.rfc.trim()) {
      toastError({ title: "Proveedor incompleto", message: "Nombre y RFC del proveedor son obligatorios." });
      return;
    }
    // La clave de la OC alimenta las entradas (FK lógica).
    const payload: OrdenCompraInput = {
      ...form,
      entradas: form.entradas.map((e) => ({ ...e, claveOrdenCompra: clave.trim() })),
    };
    if (esNuevo) crear.mutate({ clave: clave.trim(), input: payload });
    else actualizar.mutate({ clave: clave.trim(), input: payload });
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Heading order={5}>{esNuevo ? "Nueva orden de compra" : `Editar OC ${inicial?.clave}`}</Heading>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Input label="Clave" required readOnly={!esNuevo} value={clave} onChange={(e) => setClave(e.currentTarget.value)} className="font-mono" />
          <Input label="División" value={form.division ?? ""} onChange={(e) => upd({ division: e.currentTarget.value || null })} />
          <Input label="Estatus" value={form.estatus ?? ""} onChange={(e) => upd({ estatus: e.currentTarget.value || null })} />
          <Input label="Fecha (ISO, opcional)" placeholder="2026-03-01" value={form.fecha ?? ""} onChange={(e) => upd({ fecha: e.currentTarget.value || null })} />
          <Input label="Moneda" required value={form.moneda} onChange={(e) => upd({ moneda: e.currentTarget.value })} />
          <NumberInput label="Subtotal" hideControls decimals={4} value={form.subTotal} onChange={(v) => upd({ subTotal: Number(v) || 0 })} />
          <NumberInput label="Impuestos" hideControls decimals={4} value={form.impuestos} onChange={(v) => upd({ impuestos: Number(v) || 0 })} />
          <NumberInput label="Total" hideControls decimals={4} value={form.total} onChange={(v) => upd({ total: Number(v) || 0 })} />
        </div>

        <ProveedorFields value={form.proveedor} onChange={(p) => upd({ proveedor: p })} />

        <Textarea label="Observaciones" rows={1} value={form.observaciones ?? ""} onChange={(e) => upd({ observaciones: e.currentTarget.value || null })} />

        <Divider label="Conceptos" />
        <ConceptosEditor value={form.conceptos} onChange={(cs) => upd({ conceptos: cs })} readOnly />

        <Divider label="Entradas de almacén" />
        <EntradasEditor
          value={form.entradas}
          onChange={(es) => upd({ entradas: es })}
          claveOrdenCompra={clave}
          moneda={form.moneda}
          onImportarXml={importarEntrada}
          importando={importando}
          readOnly
        />

        <Group justify="flex-end">
          <Button variant="default" leftSection={<IconX size={14} />} onClick={onDone}>Cancelar</Button>
          <Button leftSection={<IconDeviceFloppy size={14} />} loading={guardando} onClick={submit}>
            {esNuevo ? "Crear orden" : "Guardar cambios"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
